import { useEffect, useMemo, useState } from "react";

function getSnapshot() {
  if (typeof window === "undefined") {
    return {
      width: 1280,
      height: 800,
      orientation: "landscape",
      touch: false,
      coarsePointer: false,
      hover: true,
      standalone: false,
      userAgent: "",
      platform: "",
      cameraSupported: false,
    };
  }

  const nav = window.navigator || {};
  const width = Math.round(window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 1280);
  const height = Math.round(window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 800);
  const orientation = width > height ? "landscape" : "portrait";
  const touch = "ontouchstart" in window || (nav.maxTouchPoints || 0) > 0;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches || false;
  const hover = window.matchMedia?.("(hover: hover)")?.matches || false;
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || nav.standalone === true;

  return {
    width,
    height,
    orientation,
    touch,
    coarsePointer,
    hover,
    standalone,
    userAgent: nav.userAgent || "",
    platform: nav.platform || "",
    cameraSupported: !!nav.mediaDevices?.getUserMedia,
  };
}

function classify(snapshot) {
  const ua = snapshot.userAgent.toLowerCase();
  const platform = snapshot.platform.toLowerCase();
  const minSide = Math.min(snapshot.width, snapshot.height);
  const maxSide = Math.max(snapshot.width, snapshot.height);
  const isIpad = /ipad/.test(ua) || (platform.includes("mac") && snapshot.touch && maxSide >= 768);
  const isIphone = /iphone|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isTabletUa = /tablet|kindle|silk/.test(ua) || (isAndroid && minSide >= 600);
  const isPhoneUa = /mobile|iphone|ipod/.test(ua) && !isTabletUa;

  let type = "desktop";
  if (snapshot.width < 640 || minSide < 600) type = "phone";
  if (snapshot.width >= 640 && snapshot.width < 980) type = "tablet";
  if (isIpad || isTabletUa || (snapshot.touch && minSide >= 600 && maxSide < 1400)) type = "tablet";
  if (isIphone || isPhoneUa || (snapshot.touch && minSide < 600)) type = "phone";
  if (!snapshot.touch && snapshot.width < 1180) type = "laptop";
  if (!snapshot.touch && snapshot.width >= 1180 && snapshot.width < 1440) type = "laptop";
  if (snapshot.width < 640) type = "phone";

  const family = isIpad ? "iPad" : isIphone ? "iPhone" : isAndroid ? "Android" : platform.includes("mac") ? "Mac" : platform.includes("win") ? "Windows" : "Web";
  const label = type === "phone" ? `${family} phone` : type === "tablet" ? `${family} tablet` : type === "laptop" ? `${family} laptop` : `${family} desktop`;

  return {
    ...snapshot,
    type,
    family,
    label,
    isPhone: type === "phone",
    isTablet: type === "tablet",
    isMobile: type === "phone" || type === "tablet",
    isLaptop: type === "laptop",
    isDesktop: type === "desktop",
    isAppleMobile: isIpad || isIphone,
    prefersCompactNav: type === "phone" || type === "tablet",
    supportsCameraStudio: snapshot.cameraSupported && (snapshot.touch || snapshot.coarsePointer),
  };
}

export function useDeviceProfile() {
  const [snapshot, setSnapshot] = useState(getSnapshot);

  useEffect(() => {
    const update = () => setSnapshot(getSnapshot());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    const coarse = window.matchMedia?.("(pointer: coarse)");
    const hover = window.matchMedia?.("(hover: hover)");
    coarse?.addEventListener?.("change", update);
    hover?.addEventListener?.("change", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      coarse?.removeEventListener?.("change", update);
      hover?.removeEventListener?.("change", update);
    };
  }, []);

  return useMemo(() => classify(snapshot), [snapshot]);
}
